import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { AppBar, Divider, IconButton, Stack, Toolbar, Tooltip, Typography } from '@mui/material';
import { useLanguageSelector } from '@retail/app/hooks';
import { MgItemSearchAutocomplete } from '@retail/products/containers';
import { BrandSelect, BrandSelectOption, LanguageSwitch } from '@retail/components';
import { GlobalDraggable, HelpCenter, ThemeModeToggle } from '@shared/components';
import { mgPrisRoutes } from '@retail/mgpris/config';
import {
  useIsFeatureEnabled,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import { mgprisHeaderHeight } from '@shared/styles';
import { OrgUnitAssortmentSelectContainer } from '@retail/app/containers';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useSharedTranslation } from '@shared/i18n';
import dayjs from 'dayjs';
import { HelpOutline } from '@mui/icons-material';
import { useDisclosure } from '@shared/hooks';
import { useWindowSize } from 'react-use';
import {
  CategoryArticlesContainer,
  HelpArticleContainer,
  SearchContainer,
  TopLevelCategoriesContainer,
} from '@retail/help-center/containers';
import { useAppTFunction } from '@retail/app/i18n';
import {
  ChangelogEntriesIconMenuDropdown,
  LatestChangeLogEntryPopupContainer,
} from '@retail/changelog/containers';
import { MgSupplierItemSearchResultDTO } from '@retail/products/types';
import { supplierItemSearchParam } from '@retail/utils';
import { SunMoonIcon } from '@shared/custom-icons';

const handleClass = 'help-center-drag-handle';

interface Props {
  appLogo: ReactElement;
}

export function Header({ appLogo }: Props) {
  const { switchLanguage } = useLanguageSelector();
  const { appTenant, selectedOrgUnitAssortment } = useSelectedOrgUnit();

  const t = useAppTFunction();
  const { t: sharedT } = useSharedTranslation();
  const {
    i18n: { language },
  } = useTranslation();

  const { version, buildTime } = useMgPrisConfig();
  const { width: screenWidth } = useWindowSize();

  const { availableBrands, selectedBrand, switchBrand } = useUserBrands();
  const isHelpCenterEnabled = useIsFeatureEnabled('help-center');

  const {
    isOpen: showHelpCenter,
    onToggle: onToggleHelpCenter,
    onClose: onCloseHelpCenter,
  } = useDisclosure();

  const brandOptions: BrandSelectOption[] = useMemo(
    () =>
      availableBrands.map(({ name, brandId, tenant }) => ({
        name,
        key: brandId,
        countryCode: tenant.countryCode,
      })),
    [availableBrands]
  );

  const changeBrand = useCallback(
    (brandKey: string) => {
      const newBrand = availableBrands.find(({ brandId }) => brandId === brandKey);
      if (newBrand) {
        switchBrand(newBrand);
      }
    },
    [availableBrands, switchBrand]
  );

  const navigate = useNavigate();
  const onMgItemSearchResultClick = useCallback(
    (mgItem: MgSupplierItemSearchResultDTO) =>
      navigate(
        `${mgPrisRoutes.items.item.getFullLinkPath({
          itemNumber: mgItem.mgItemNumber,
        })}?${supplierItemSearchParam}=${
          appTenant.countryCode === 'SE' ? mgItem.finfoNumber : mgItem.nobbNumber
        }`,
        {
          state: { hiddenBreadcrumbs: true },
        }
      ),
    [navigate, appTenant.countryCode]
  );

  const { buildDate, buildTimeFormatted } = useMemo(
    () => ({
      buildDate: buildTime && dayjs.unix(buildTime).format('l'),
      buildTimeFormatted: buildTime && dayjs.unix(buildTime).format('hh:mm:ss'),
    }),
    [buildTime]
  );

  const headerIconStyle = {
    color: 'grey.500',
    height: '34px',
    width: '34px',
  };

  return (
    <AppBar position="fixed" sx={{ height: mgprisHeaderHeight, bgcolor: 'background.paper' }}>
      <Toolbar
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" alignSelf="stretch" paddingY={2}>
          <BrandSelect
            brands={brandOptions}
            selectedBrandKey={selectedBrand.brandId}
            onBrandSelect={changeBrand}
          />

          <Divider orientation="vertical" sx={{ borderColor: 'background.default', marginX: 3 }} />

          <MgItemSearchAutocomplete
            onItemClick={onMgItemSearchResultClick}
            sx={{ width: '300px' }}
          />
        </Stack>

        <Stack direction="row" marginLeft={2} height="100%" gap={2}>
          <Stack alignItems="center" direction="row">
            <OrgUnitAssortmentSelectContainer />

            <Tooltip
              arrow
              title={
                <Stack px={2}>
                  <Typography variant="body2">{`${sharedT('shared.version')}: ${version.slice(
                    -6
                  )}`}</Typography>
                  {!!buildDate && <Typography variant="body2">{buildDate}</Typography>}
                  {!!buildTimeFormatted && (
                    <Typography variant="body2">{buildTimeFormatted}</Typography>
                  )}
                  <Typography variant="body2">
                    {selectedOrgUnitAssortment.orgType.getName()}
                  </Typography>
                  {selectedOrgUnitAssortment.userRole === 'SUPERUSER' && (
                    <>
                      <Typography variant="body2">{`assortment-id: ${selectedOrgUnitAssortment.assortmentId}`}</Typography>
                      <Typography variant="body2">{`draftAssortment-id: ${selectedOrgUnitAssortment.draftAssortmentId}`}</Typography>
                      <Typography variant="body2">{`orgUnit-id: ${selectedOrgUnitAssortment.orgUnitId}`}</Typography>
                    </>
                  )}
                </Stack>
              }
            >
              {appLogo}
            </Tooltip>

            <ThemeModeToggle variant="menu" size="medium" icon={<SunMoonIcon />} />

            <ChangelogEntriesIconMenuDropdown />

            {isHelpCenterEnabled && (
              <IconButton onClick={onToggleHelpCenter} aria-label={t('helpCenter.open')}>
                <HelpOutline sx={headerIconStyle} />
              </IconButton>
            )}

            <LanguageSwitch
              selectedLanguage={language}
              switchLanguage={switchLanguage}
              languages={['no', 'se']}
            />
          </Stack>
        </Stack>
      </Toolbar>

      {showHelpCenter && (
        <GlobalDraggable
          handle={`.${handleClass}`}
          defaultPosition={{ x: screenWidth - 600, y: 100 }}
        >
          <HelpCenter
            onClose={onCloseHelpCenter}
            titleClassName={handleClass}
            CategoryArticlesComponent={CategoryArticlesContainer}
            TopLevelCategoriesComponent={TopLevelCategoriesContainer}
            HelpArticleComponent={HelpArticleContainer}
            SearchContainerComponent={SearchContainer}
          />
        </GlobalDraggable>
      )}
      <LatestChangeLogEntryPopupContainer />
    </AppBar>
  );
}
